var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import * as React from "react";
import { useGateValue } from "@statsig/react-bindings";
import { getPartnerSafeMode, isMessagingBffConfigured, setPartnerSafeMode } from "@/api/messaging/messaging";
import { SAFE_MODE_GATE } from "@/utilities/safeMode/safeMode";
import useAdminStore from "@/services/store/useAdminStore";
import useErrorHandler from "@/utilities/hooks/useErrorHandler";
import translate from "@/utilities/i18n/getTranslate";
import { k } from "@/utilities/i18n";
/**
 * Partner-level Safe Mode switch.
 *
 * Optimistic without a cache library: `requested` is shown while the write is
 * in flight, and the POST response carries the authoritative new value, so
 * there is no refetch and therefore no window in which a stale value could
 * flash back. On failure `serverValue` is never touched, so clearing
 * `requested` reverts the switch on its own.
 */
var usePartnerSafeMode = function (partnerId) {
    var adminNotification = useAdminStore().adminNotification;
    var handleError = useErrorHandler();
    var isGateOn = useGateValue(SAFE_MODE_GATE);
    var available = Boolean(isGateOn && isMessagingBffConfigured && partnerId);
    // undefined means "unknown". Never assume "off" - a switch rendering off
    // because a read failed tells the partner protection is disabled when it may
    // well be on.
    var _a = __read(React.useState(undefined), 2), serverValue = _a[0], setServerValue = _a[1];
    var _b = __read(React.useState(null), 2), requested = _b[0], setRequested = _b[1];
    var _c = __read(React.useState(false), 2), isSaving = _c[0], setIsSaving = _c[1];
    var _d = __read(React.useState(false), 2), isConfirmOpen = _d[0], setIsConfirmOpen = _d[1];
    React.useEffect(function () {
        if (!available || !partnerId)
            return;
        var cancelled = false;
        var read = function () { return __awaiter(void 0, void 0, void 0, function () {
            var response, err_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, getPartnerSafeMode({ partnerId: partnerId })];
                    case 1:
                        response = _a.sent();
                        if (!cancelled)
                            setServerValue(response.safeModeEnabled);
                        return [3 /*break*/, 3];
                    case 2:
                        err_1 = _a.sent();
                        // Suppressed on purpose: track it, but show no toast and leave the
                        // value unknown so the whole row stays hidden.
                        if (!cancelled)
                            handleError(err_1, null, "Reading partner Safe Mode failed");
                        return [3 /*break*/, 3];
                    case 3: return [2 /*return*/];
                }
            });
        }); };
        read();
        return function () {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [available, partnerId]);
    var setEnabled = function (next) { return __awaiter(void 0, void 0, void 0, function () {
        var response, err_2;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!partnerId || isSaving)
                        return [2 /*return*/];
                    setRequested(next);
                    setIsSaving(true);
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, 4, 5]);
                    return [4 /*yield*/, setPartnerSafeMode({ partnerId: partnerId }, { enabled: next })];
                case 2:
                    response = _a.sent();
                    setServerValue(response.safeModeEnabled);
                    adminNotification.add({
                        type: adminNotification.types.SUCCESS,
                        message: response.safeModeEnabled
                            ? translate(k.DASH_safeModeEnabledToast)
                            : translate(k.DASH_safeModeDisabledToast)
                    });
                    return [3 /*break*/, 5];
                case 3:
                    err_2 = _a.sent();
                    // Writes do toast - unlike reads, the partner asked for this to happen.
                    handleError(err_2, { message: translate(k.ERR_safeModeUpdateError) }, "Updating partner Safe Mode failed");
                    return [3 /*break*/, 5];
                case 4:
                    setIsSaving(false);
                    setRequested(null);
                    setIsConfirmOpen(false);
                    return [7 /*endfinally*/];
                case 5: return [2 /*return*/];
            }
        });
    }); };
    var displayedValue = isSaving && requested !== null ? requested : serverValue;
    return {
        // Hide the row entirely while the state cannot be read.
        show: available && serverValue !== undefined,
        isEnabled: Boolean(displayedValue),
        isSaving: isSaving,
        isConfirmOpen: isConfirmOpen,
        setIsConfirmOpen: setIsConfirmOpen,
        confirm: function () { return setEnabled(!serverValue); }
    };
};
export default usePartnerSafeMode;
//# sourceMappingURL=data:application/json;base64,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